import type { CleanRenderResult } from '@sorrem-client/types';
import type { Vector2 } from '@sorrem/shared';
import type { CharacterPreviewCommand, CharacterPreviewMessage } from './character-preview/character-preview-protocol';
import type { CharacterDebugCommand, DebugSceneMessage, MapDebugCommand, VfxDebugCommand } from './debug/debug-scene-protocol';
import type { GameSceneCommand, GameSceneMessage } from './game-scene-protocol';
import type { CrucibleGatheringSceneCommand, CrucibleGatheringSceneMessage } from './gathering-chamber/gathering-protocol';
import type { ItemEditorCommand, ItemEditorMessage } from './item-editor/item-editor-protocol';
import type { ItemFramesCommand, ItemFramesMessage } from './item-frames/item-frames-protocol';
import type { PropEditorCommand, PropEditorMessage } from './prop-editor/prop-editor-protocol';

/** What a scene shows when its GPU device was lost and it cannot start again (#344). */
export const DEVICE_LOST_MESSAGE = 'The graphics device was lost. Reload the page to draw this again.';

/*
 * The render worker protocol's core: what every scene's runtime
 * (`runRenderWorker`) handles alike, and the unions of every scene's own
 * commands and messages. Each scene's live in its own module, re-exported
 * here: the game's (game-scene-protocol.ts), the gathering's, the character
 * preview's, the item frames', the item and prop editors' and the debuggers'.
 */
export type * from './character-preview/character-preview-protocol';
export type * from './debug/debug-scene-protocol';
export type * from './game-scene-protocol';
export type * from './gathering-chamber/gathering-protocol';
export type * from './item-editor/item-editor-protocol';
export type * from './item-frames/item-frames-protocol';
export type * from './prop-editor/prop-editor-protocol';

/**
 * Static, non-size worker config. Render target size is deliberately not
 * here - it's carried by the `initialize` command instead, since the real
 * display canvas's size generally isn't known yet at `new Worker(...)` time
 * (see `render-worker.ts`'s own doc comment on deferred construction).
 */
export interface RenderWorkerConfig {
    readonly enableMetrics: boolean;
}

/**
 * A pickable thing a scene's `resolvePointer` hook found under the cursor.
 * `kind` distinguishes ground/entity/map-object hits, which the live game
 * needs to resolve different pointer actions (move/attack/interact); the
 * debug harness's demo scene only ever produces `'entity'` hits.
 */
export interface PickedTarget {
    readonly kind: 'ground' | 'entity' | 'mapObject' | 'worldObject';
    readonly id: string;
    readonly worldPosition: { readonly x: number; readonly y: number; readonly z: number };
}

/** A `pick-result`: what the click hit, if anything, and the floor point under it either way. */
export interface PickResult {
    readonly target: PickedTarget | null;
    readonly groundPosition: Vector2 | null;
}

/**
 * Scene-specific toggles a caller can push into a running worker. Grown from
 * a single `wireframe` field (enough to prove the mechanism generically)
 * once a real consumer - game.ts's debug overlay settings - needed more.
 */
export interface RenderOptions {
    readonly wireframe: boolean;
    // Map debugger view toggles.
    readonly showAreaOutlines: boolean;
    readonly showAreaNames: boolean;
    readonly showTileGrid: boolean;
    readonly showTileIds: boolean;
    readonly showTileCoords: boolean;
    readonly hideFloor: boolean;
    readonly hideWall: boolean;
}

/**
 * What a scene's runtime (`runRenderWorker`) handles itself, alike for
 * every scene: starting up, size, the pointer, render options, frames on
 * demand and clean renders.
 */
export type RenderWorkerCoreCommand =
    /**
     * Always the first command a worker receives (sent synchronously by
     * `createRenderWorkerSession` before the session is handed back to its
     * caller) - constructs the worker's Engine/scene on `canvas`, at the size
     * the canvas already has. See `render-worker.ts`'s doc comment on why
     * construction is deferred to this command instead of happening eagerly at
     * some default/guessed size.
     *
     * `canvas` is the on-screen canvas, transferred to the worker with
     * `transferControlToOffscreen()`. The worker renders straight into it and
     * the browser presents it, so no frames travel back to the main thread.
     */
    | { readonly type: 'initialize'; readonly canvas: OffscreenCanvas }
    /**
     * Resizes the worker's OffscreenCanvas/engine to match the real display
     * canvas - sent whenever a caller's display canvas size changes *after*
     * `initialize`. Without this, the worker keeps rendering at whichever
     * size it was constructed with regardless of the display canvas's
     * current size, which both blurs the frame (upscaled to fill a larger
     * canvas) and breaks pointer picking (the page converts click
     * coordinates against the drawing-buffer size it last sent, which the
     * worker's picking math must agree with).
     */
    | { readonly type: 'resize'; readonly width: number; readonly height: number }
    /** Where the pointer is, in canvas drawing-buffer pixels. */
    | { readonly type: 'pointer-move'; readonly position: Vector2 }
    /** A press at `position` (drawing-buffer pixels), answered by one `pick-result` with the same `requestId`. */
    | { readonly type: 'pointer-down'; readonly position: Vector2; readonly requestId: string }
    | { readonly type: 'set-render-options'; readonly options: Partial<RenderOptions> }
    /**
     * Stops (or resumes) calling `update`/`scene.render()` every frame. A
     * scene that's currently a static snapshot (e.g. the Character Home
     * background with animations disabled) sends `enabled: false` once its
     * last `render-frame` has painted, so the worker's `requestAnimationFrame`
     * loop keeps ticking (cheap) but does no rendering work (the expensive
     * part) until told otherwise. Resizing always forces one frame regardless
     * of this flag, so a frozen scene still redraws at its new size.
     */
    | { readonly type: 'set-continuous-rendering'; readonly enabled: boolean }
    /** Renders exactly one frame on the next tick, then goes back to idle. Used to refresh a static scene (continuous rendering off) after something changes it, e.g. a new character finished loading. */
    | { readonly type: 'render-frame' }
    /** A developer snapshot (#303): draws the scene alone at `scale` times the canvas size; answered by `clean-render`. */
    | { readonly type: 'capture-clean-render'; readonly requestId: number; readonly scale: number };

/** Every command a scene handles past its runtime: some scene's `onCommand`. Each scene takes its own slice (Q4). */
export type RenderWorkerSceneCommand =
    | GameSceneCommand
    | CrucibleGatheringSceneCommand
    | CharacterPreviewCommand
    | CharacterDebugCommand
    | ItemFramesCommand
    | ItemEditorCommand
    | PropEditorCommand
    | MapDebugCommand
    | VfxDebugCommand;

/**
 * Wire format for main-thread -> worker commands. `pointer-move` is
 * latest-wins by design (the worker only ever acts on the most recently
 * received one, resolved once per rendered frame) - see render-worker.ts.
 * `pointer-down` is handled immediately and individually, correlated by
 * `requestId` so a click always gets exactly one `pick-result` response.
 * `scene-state` is also latest-wins (posted once per game tick; the worker
 * always renders against the most recent snapshot it has). `set-equipment`
 * and `load-map` are discrete, one-shot commands mirroring the existing
 * event-driven re-equip/map-load flow in game.ts.
 */
export type RenderWorkerCommand = RenderWorkerCoreCommand | RenderWorkerSceneCommand;

/** Whether the runtime handles `command` itself, rather than its scene. */
export function isRenderWorkerCoreCommand(command: RenderWorkerCommand): command is RenderWorkerCoreCommand {
    return CORE_COMMAND_TYPES.has(command.type);
}

const CORE_COMMAND_TYPES: ReadonlySet<RenderWorkerCommand['type']> = new Set<RenderWorkerCoreCommand['type']>([
    'initialize', 'resize', 'pointer-move', 'pointer-down', 'set-render-options', 'set-continuous-rendering', 'render-frame', 'capture-clean-render',
]);

/** What the live game sends its scene: the runtime's commands and the game scene's own. */
export type GameRenderCommand = RenderWorkerCoreCommand | GameSceneCommand;

/** The crucible's scene (`createCrucibleSceneBuilder`): the game's, with the gathering in front. */
export type CrucibleSceneCommand = GameSceneCommand | CrucibleGatheringSceneCommand;

/** What every scene's runtime tells its page alike: hovers, picks, errors and clean renders. */
export type RenderWorkerCoreMessage =
    | { readonly type: 'hover-change'; readonly target: PickedTarget | null }
    /** `groundPosition` is the floor-plane point under the click, hit or miss (see `pointer-ground`). */
    | { readonly type: 'pick-result'; readonly requestId: string; readonly target: PickedTarget | null; readonly groundPosition: Vector2 | null }
    /**
     * The floor-plane point under the latest `pointer-move`, through the live
     * camera. Posted once per rendered frame when it changes, so it follows
     * the camera as well as the pointer.
     */
    | { readonly type: 'pointer-ground'; readonly position: Vector2 | null }
    | { readonly type: 'error'; readonly message: string }
    /**
     * The GPU device behind the worker's engine was lost (a GPU process reset
     * or driver recovery). The worker has stopped drawing for good; the
     * session starts a fresh worker on a fresh canvas (#344).
     */
    | { readonly type: 'device-lost' }
    /** The answer to `capture-clean-render`; the session resolves its own request with it, so no callback sees it. */
    | { readonly type: 'clean-render'; readonly requestId: number; readonly result: CleanRenderResult };

/**
 * Wire format for worker -> main-thread messages. Defined once here so
 * render-worker.ts (sender) and render-manager.ts (receiver) can't drift out
 * of sync with each other about the message shape.
 */
export type RenderWorkerMessage =
    | RenderWorkerCoreMessage
    | GameSceneMessage
    | CrucibleGatheringSceneMessage
    | CharacterPreviewMessage
    | ItemFramesMessage
    | ItemEditorMessage
    | PropEditorMessage
    | DebugSceneMessage;
